import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, FileText, Percent, Globe } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What should a Nigerian invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A Nigerian invoice should include: your business name and address; your TIN (Tax Identification Number); the client name and address; a unique invoice number; the invoice date; a description of goods or services; the amount; and your payment details. VAT-registered businesses must also include their VAT registration number, the 7.5% VAT amount, and the VAT-inclusive total.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the VAT rate in Nigeria?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The VAT rate in Nigeria is 7.5%, which was increased from 5% in 2020. It applies to most goods and services. Some items are VAT-exempt, including basic food items, medical supplies, and educational materials. Exported services are zero-rated. VAT registration is mandatory for businesses with annual turnover exceeding NGN 25 million.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do Nigerian freelancers invoice international clients?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Nigerian freelancers providing services to overseas clients typically invoice in USD. Export of services to non-residents is zero-rated for VAT — no 7.5% VAT is charged. Use the international invoice type to bill in USD, GBP, or EUR with your Nigerian bank details. Most Nigerian freelancers receive international payments via Payoneer, Wise, or domiciliary bank accounts.',
      },
    },
    {
      '@type': 'Question',
      name: 'When do Nigerian businesses need to register for VAT?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'VAT registration with FIRS (Federal Inland Revenue Service) is compulsory for businesses with annual taxable turnover exceeding NGN 25 million. Below this threshold, businesses are exempt from VAT obligations. However, all businesses must obtain a TIN from FIRS regardless of VAT registration status.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is this invoice generator free for Nigerian users?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic is completely free for Nigerian freelancers and businesses. Create professional invoices in USD, NGN, or any currency. Download a clean PDF instantly — no sign-up required for up to 5 invoices per month.',
      },
    },
  ],
};

const invoiceTypes = [
  {
    icon: Globe,
    title: 'International Invoice',
    subtitle: 'For billing overseas clients in USD',
    features: ['USD, GBP, EUR billing', 'Zero-rated export format', 'SWIFT / domiciliary account details', 'Multi-currency support', 'Professional PDF'],
    to: '/create/international',
    color: 'emerald',
    btnClass: 'bg-emerald-600 hover:bg-emerald-700',
  },
  {
    icon: FileText,
    title: 'Standard Invoice',
    subtitle: 'For local clients in NGN',
    features: ['NGN currency', 'Custom line items', 'Logo upload', 'TIN number field', 'Payment terms'],
    to: '/create/freelancer',
    color: 'blue',
    btnClass: 'bg-blue-600 hover:bg-blue-700',
  },
  {
    icon: Percent,
    title: 'VAT Invoice',
    subtitle: 'For VAT-registered businesses',
    features: ['7.5% VAT auto-calculated', 'VAT registration number field', 'Net + VAT + total', 'FIRS-ready format', 'NGN currency'],
    to: '/create/vat',
    color: 'indigo',
    btnClass: 'bg-indigo-600 hover:bg-indigo-700',
  },
];

export default function InvoiceGeneratorNigeriaPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Invoice Generator Nigeria: Free Invoice Maker for Nigerian Freelancers | Invilic"
        description="Free invoice generator for Nigerian freelancers and businesses. Create invoices in USD, NGN, and more. 7.5% VAT invoices supported. PDF download free, no sign-up."
        canonical="https://www.invilic.com/invoice-generator-nigeria"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <span className="inline-block bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            🇳🇬 Nigeria
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Invoice Generator Nigeria
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Free invoice generator for Nigerian freelancers and businesses. Create professional invoices in USD, NGN, or any currency. Bill international clients with zero-rated export invoices or local clients in NGN. Download a clean PDF in seconds.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/create/international" className="inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-emerald-600/20 transition-all duration-200">
              Bill International Clients <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/create/freelancer" className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-blue-600/20 transition-all duration-200">
              Create NGN Invoice <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">Which Invoice Do You Need?</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {invoiceTypes.map(({ icon: Icon, title, subtitle, features, to, color, btnClass }) => (
              <div key={title} className={`border border-${color}-100 dark:border-${color}-800/30 rounded-2xl p-6 bg-${color}-50 dark:bg-${color}-900/10`}>
                <div className={`w-10 h-10 rounded-xl bg-${color}-100 dark:bg-${color}-900/40 flex items-center justify-center mb-4`}>
                  <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1">{title}</h3>
                <p className={`text-xs text-${color}-700 dark:text-${color}-300 mb-4`}>{subtitle}</p>
                <ul className="space-y-2 mb-5">
                  {features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <CheckCircle className={`w-3.5 h-3.5 text-${color}-500 flex-shrink-0`} /> {f}
                    </li>
                  ))}
                </ul>
                <Link to={to} className={`inline-flex items-center justify-center gap-1.5 w-full px-4 py-2.5 ${btnClass} text-white text-sm font-semibold rounded-xl transition-all`}>
                  Create Free <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16 p-8 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-5">International vs Local Invoice: Which Do You Need?</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <div className="font-semibold text-emerald-700 dark:text-emerald-400 mb-3">Use an International Invoice if:</div>
              {[
                'Your client is overseas (US, UK, Europe, Canada, etc.)',
                'You bill in USD, GBP, EUR, or another foreign currency',
                'Your services are zero-rated for export — no 7.5% VAT applies',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
            <div>
              <div className="font-semibold text-blue-700 dark:text-blue-400 mb-3">Use an NGN Invoice if:</div>
              {[
                'Your client is based in Nigeria and pays in NGN',
                'You provide services to local Nigerian businesses',
                'You need to include TIN and local business details',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Nigeria Invoice Generator: Frequently Asked Questions</h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">{acceptedAnswer.text}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Create Your Nigerian Invoice for Free</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF in USD, NGN, or any currency. No account required.</p>
          <Link to="/create/international" className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-emerald-600/20 transition-all duration-200 text-lg">
            Create Invoice Free <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
    </div>
  );
}
